<template>
  <div class="view-container">
    <div class="main-layout">
      <div class="left-panel">
        <div class="panel-header">
          <h4>文档目录</h4>
          <button @click="openAddCategoryDialog" class="btn btn-primary btn-xs"><i class="fas fa-plus"></i></button>
        </div>
        <div class="panel-body">
          <table class="tree-table">
            <thead><tr><th>目录名称</th><th width="60">数量</th></tr></thead>
            <tbody>
              <template v-for="node in categoryTree" :key="node.id">
                <tr class="tree-row" :class="{ selected: selectedCategoryId === node.id }" @click="selectCategory(node)">
                  <td>
                    <span class="tree-name-cell">
                      <span v-if="node.children && node.children.length > 0" class="expand-icon" :class="{ expanded: expandedIds.includes(node.id) }" @click.stop="toggleExpand(node.id)"><i class="fas fa-caret-right"></i></span>
                      <span v-else class="expand-placeholder"></span>
                      <i class="fas" :class="node.children && node.children.length > 0 ? 'fa-folder' : 'fa-folder-open'"></i>
                      <span class="tree-name">{{ node.name }}</span>
                    </span>
                  </td>
                  <td class="count-cell">{{ node.docCount || 0 }}</td>
                </tr>
                <template v-if="node.children && node.children.length > 0 && expandedIds.includes(node.id)">
                  <tr v-for="child in node.children" :key="child.id" class="tree-row sub-row" :class="{ selected: selectedCategoryId === child.id }" @click="selectCategory(child)">
                    <td><span class="tree-name-cell" style="padding-left:16px"><i class="fas fa-folder-open"></i><span class="tree-name">{{ child.name }}</span></span></td>
                    <td class="count-cell">{{ child.docCount || 0 }}</td>
                  </tr>
                </template>
              </template>
            </tbody>
          </table>
        </div>
      </div>

      <div class="right-panel">
        <div class="panel-body">
          <div v-if="selectedCategoryId">
            <div class="right-header">
              <div class="header-left">
                <h5>{{ selectedCategoryName }} 文档列表</h5>
                <div class="header-search">
                  <input v-model="docSearch" class="form-control form-control-sm" placeholder="搜索文档...">
                  <select v-model="docTypeFilter" class="form-control form-control-sm" style="width:120px">
                    <option value="">全部类型</option><option value="doc">Word</option><option value="xls">Excel</option><option value="pdf">PDF</option><option value="ppt">PPT</option>
                  </select>
                </div>
              </div>
              <button @click="openAddDoc" class="btn btn-primary btn-xs"><i class="fas fa-plus"></i> 新增文档</button>
            </div>

            <div class="doc-list">
              <div v-for="doc in filteredDocs" :key="doc.id" class="doc-item" :class="{ active: selectedDocId === doc.id }" @click="selectDoc(doc)">
                <div class="doc-icon"><i class="fas" :class="getFileIcon(doc.type)"></i></div>
                <div class="doc-info">
                  <div class="doc-name">{{ doc.name }}</div>
                  <div class="doc-meta">
                    <span class="meta">编号: {{ doc.code }}</span>
                    <span class="meta">版本: v{{ doc.version }}</span>
                    <span class="meta">状态: <span class="status-badge" :class="doc.status">{{ statusLabels[doc.status] || doc.status }}</span></span>
                    <span class="meta">{{ doc.author }} · {{ doc.updateTime }}</span>
                  </div>
                </div>
                <div class="doc-actions" @click.stop>
                  <button @click="previewDoc(doc)" class="action-btn preview" title="预览"><i class="fas fa-eye"></i></button>
                  <button @click="openEditDoc(doc)" class="action-btn edit" title="编辑"><i class="fas fa-edit"></i></button>
                  <button @click="downloadDoc(doc)" class="action-btn download" title="下载"><i class="fas fa-download"></i></button>
                  <button @click="deleteDoc(doc)" class="action-btn delete" title="删除"><i class="fas fa-trash"></i></button>
                </div>
              </div>
              <div v-if="filteredDocs.length === 0" class="empty-list">该目录暂无文档</div>
            </div>
          </div>
          <div v-else class="empty-state"><i class="fas fa-book-open"></i><p>请选择一个文档目录</p><p class="hint">在左侧选择目录查看文档列表</p></div>
        </div>
      </div>

      <!-- 右侧详情抽屉 -->
      <transition name="slide-left">
        <div v-if="selectedDoc" class="detail-drawer">
          <div class="drawer-header">
            <h5><i class="fas" :class="getFileIcon(selectedDoc.type)"></i> {{ selectedDoc.name }}</h5>
            <button @click="closeDetail" class="close-btn"><i class="fas fa-times"></i></button>
          </div>
          <div class="drawer-body">
            <div class="detail-grid">
              <div class="detail-item"><label>文档编号</label><span>{{ selectedDoc.code }}</span></div>
              <div class="detail-item"><label>版本号</label><span>v{{ selectedDoc.version }}</span></div>
              <div class="detail-item"><label>文档类型</label><span>{{ selectedDoc.type.toUpperCase() }}</span></div>
              <div class="detail-item"><label>状态</label><span class="status-badge" :class="selectedDoc.status">{{ statusLabels[selectedDoc.status] || selectedDoc.status }}</span></div>
              <div class="detail-item"><label>作者</label><span>{{ selectedDoc.author }}</span></div>
              <div class="detail-item"><label>更新时间</label><span>{{ selectedDoc.updateTime }}</span></div>
              <div class="detail-item"><label>有效期至</label><span>{{ selectedDoc.expireDate || '-' }}</span></div>
              <div class="detail-item"><label>文件大小</label><span>{{ selectedDoc.size || '-' }}</span></div>
            </div>
            <div class="detail-item full-width"><label>关联流程</label><span>{{ selectedDoc.relatedFlow || '无' }}</span></div>
            <div class="detail-item full-width"><label>摘要</label><span>{{ selectedDoc.summary || '暂无摘要' }}</span></div>

            <div class="version-history" v-if="selectedDoc.versions && selectedDoc.versions.length > 0">
              <h6>版本历史</h6>
              <div v-for="v in selectedDoc.versions" :key="v.version" class="version-row">
                <span class="version-tag">v{{ v.version }}</span>
                <span>{{ v.changeDesc }}</span>
                <span class="version-meta">{{ v.author }} · {{ v.date }}</span>
              </div>
            </div>

            <div class="drawer-actions">
              <button @click="previewDoc(selectedDoc)" class="btn btn-primary btn-block">
                <i class="fas fa-eye"></i> 预览文档
              </button>
              <button @click="downloadDoc(selectedDoc)" class="btn btn-default btn-block">
                <i class="fas fa-download"></i> 下载文档
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>

    <div v-if="showDocModal" class="modal-overlay" @click.self="showDocModal=false">
      <div class="modal-dialog" style="width:700px">
        <div class="modal-header"><h4>{{ editingDoc ? '编辑文档' : '新增文档' }}</h4><button @click="showDocModal=false" class="modal-close">&times;</button></div>
        <div class="modal-body">
          <div class="form-row"><div class="form-group"><label>文档名称 *</label><input v-model="docForm.name" class="form-control" placeholder="如: 采购控制程序"></div><div class="form-group"><label>文档编号</label><input v-model="docForm.code" class="form-control" placeholder="如: QP-001"></div></div>
          <div class="form-row"><div class="form-group"><label>文档类型</label><select v-model="docForm.type" class="form-control"><option value="doc">Word</option><option value="xls">Excel</option><option value="pdf">PDF</option><option value="ppt">PPT</option></select></div><div class="form-group"><label>版本号</label><input v-model="docForm.version" class="form-control" placeholder="如: 1.0"></div></div>
          <div class="form-row"><div class="form-group"><label>作者</label><input v-model="docForm.author" class="form-control"></div><div class="form-group"><label>有效期至</label><input v-model="docForm.expireDate" type="date" class="form-control"></div></div>
          <div class="form-group"><label>关联流程</label><select v-model="docForm.relatedFlow" class="form-control"><option value="">无</option><option v-for="f in flowList" :key="f" :value="f">{{ f }}</option></select></div>
          <div class="form-group"><label>文档摘要</label><textarea v-model="docForm.summary" class="form-control" rows="3"></textarea></div>
          <div class="form-group"><label>上传文件</label><div class="upload-area" @click="triggerUpload"><i class="fas fa-cloud-upload-alt"></i><p>点击上传或拖拽文件到此处</p></div></div>
        </div>
        <div class="modal-footer"><button @click="showDocModal=false" class="btn btn-default">取消</button><button @click="saveDoc" class="btn btn-primary">保存</button></div>
      </div>
    </div>

    <div v-if="showCategoryModal" class="modal-overlay" @click.self="showCategoryModal=false">
      <div class="modal-dialog" style="width:450px">
        <div class="modal-header"><h4>{{ editingCategory ? '编辑目录' : '新增目录' }}</h4><button @click="showCategoryModal=false" class="modal-close">&times;</button></div>
        <div class="modal-body">
          <div class="form-group"><label>目录名称 *</label><input v-model="categoryForm.name" class="form-control" placeholder="如: 质量手册"></div>
          <div class="form-group"><label>上级目录</label><select v-model="categoryForm.parentId" class="form-control"><option :value="0">无（顶级目录）</option><option v-for="c in allCategories" :key="c.id" :value="c.id">{{ c.name }}</option></select></div>
        </div>
        <div class="modal-footer"><button @click="showCategoryModal=false" class="btn btn-default">取消</button><button @click="saveCategory" class="btn btn-primary">保存</button></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { 
  getCompanyFolderTree, 
  getFilesList, 
  saveFolder, 
  deleteFolder as deleteFolderAPI,
  uploadFile,
  updateFileInfo,
  deleteFile as deleteFileAPI,
  moveFile as moveFileAPI,
  copyFile as copyFileAPI,
  getDownloadUrl
} from '../api/document'

const router = useRouter()
const statusLabels = { draft: '草稿', published: '已发布', archived: '已归档', obsolete: '已废止' }

const selectedCategoryId = ref(null)
const selectedDocId = ref(null)
const expandedIds = ref([1])
const docSearch = ref('')
const docTypeFilter = ref('')
const showDocModal = ref(false)
const showCategoryModal = ref(false)
const editingDoc = ref(null)
const editingCategory = ref(null)

const docForm = reactive({ id: null, name: '', code: '', type: 'doc', version: '1.0', author: '', expireDate: '', relatedFlow: '', summary: '', categoryId: 0, status: 'draft' })
const categoryForm = reactive({ id: null, name: '', parentId: 0 })

const flowList = ref(['采购申请流程','请假申请流程','费用报销流程','合同审批流程','不合格品处理流程'])
const categoryTree = ref([])
const allDocs = ref([])
const loading = ref(false)

const selectedCategoryName = computed(() => {
  const find = (list, id) => { for (const c of list) { if (c.id === id) return c.name; if (c.children) { const r = find(c.children, id); if (r) return r } } return '' }
  return find(categoryTree.value, selectedCategoryId.value)
})

const allCategories = computed(() => {
  const list = []; const collect = (cats) => { cats.forEach(c => { if (c.id !== categoryForm.id) list.push({ id: c.id, name: c.name }); if (c.children) collect(c.children) }) }
  collect(categoryTree.value); return list
})

const selectedDoc = computed(() => allDocs.value.find(d => d.id === selectedDocId.value) || null)

const filteredDocs = computed(() => {
  let r = allDocs.value.filter(d => d.categoryId === selectedCategoryId.value)
  if (docSearch.value) r = r.filter(d => d.name.includes(docSearch.value) || d.code.includes(docSearch.value))
  if (docTypeFilter.value) r = r.filter(d => d.type === docTypeFilter.value)
  return r
})

const toggleExpand = (id) => { const idx = expandedIds.value.indexOf(id); if (idx !== -1) expandedIds.value.splice(idx, 1); else expandedIds.value.push(id) }

const selectCategory = async (node) => { 
  selectedCategoryId.value = node.id
  selectedDocId.value = null
  docSearch.value = ''
  await loadFiles(node.id)
}

const selectDoc = (doc) => { selectedDocId.value = doc.id }

const closeDetail = () => { selectedDocId.value = null }

const openAddDoc = () => { editingDoc.value = null; Object.assign(docForm, { id: null, name: '', code: '', type: 'doc', version: '1.0', author: '', expireDate: '', relatedFlow: '', summary: '' }); showDocModal.value = true }
const openEditDoc = (doc) => { editingDoc.value = doc; Object.assign(docForm, { ...doc }); showDocModal.value = true }

const saveDoc = async () => {
  if (!docForm.name.trim()) { $message.warning('请输入文档名称'); return }
  try {
    loading.value = true
    const data = {
      ...docForm,
      categoryId: selectedCategoryId.value
    }
    if (editingDoc.value) {
      await updateFileInfo(data)
    } else {
      // 实际上传需要FormData，这里只是更新元数据
      await updateFileInfo(data)
    }
    showDocModal.value = false
    await loadFiles(selectedCategoryId.value)
  } catch (error) {
    $message.error('保存失败：' + (error.message || '未知错误'))
  } finally {
    loading.value = false
  }
}

const deleteDoc = async (doc) => { 
  if (!await $confirm(`确定删除文档 "${doc.name}" 吗？`)) return
  try {
    loading.value = true
    await deleteFileAPI(doc.id)
    await loadFiles(selectedCategoryId.value)
    if (selectedDocId.value === doc.id) selectedDocId.value = null
  } catch (error) {
    $message.error('删除失败：' + (error.message || '未知错误'))
  } finally {
    loading.value = false
  }
}

const downloadDoc = (doc) => { 
  const url = getDownloadUrl(doc.id, doc.name, doc.savePath || '')
  window.open(url, '_blank')
}

const previewDoc = (doc) => {
  // 在新标签页打开预览 - 生成完整的URL
  // 使用 router.resolve 获取正确的路由路径
  const resolved = router.resolve({
    name: 'DocPreview',
    query: { guid: doc.id }
  })
  
  // 构建完整的URL（包含协议、域名、路径和hash）
  const fullUrl = `${window.location.origin}${window.location.pathname}${resolved.href}`
  console.log('预览URL:', fullUrl) // 调试日志
  window.open(fullUrl, '_blank')
}

const openAddCategoryDialog = () => { editingCategory.value = null; categoryForm.name = '', categoryForm.parentId = 0; categoryForm.id = null; showCategoryModal.value = true }

const saveCategory = async () => {
  if (!categoryForm.name.trim()) { $message.warning('请输入目录名称'); return }
  try {
    loading.value = true
    const data = {
      guid: categoryForm.id,
      name: categoryForm.name,
      parentGuid: categoryForm.parentId === 0 ? '' : categoryForm.parentId,
      folderType: 1 // 1表示企业云盘
    }
    await saveFolder(data)
    showCategoryModal.value = false
    await loadFolderTree()
  } catch (error) {
    $message.error('保存失败：' + (error.message || '未知错误'))
  } finally {
    loading.value = false
  }
}

const triggerUpload = () => { $message.info('触发文件上传') }

const getFileIcon = (type) => { 
  const icons = { 
    doc: 'fa-file-word', 
    docx: 'fa-file-word',
    xls: 'fa-file-excel',
    xlsx: 'fa-file-excel', 
    pdf: 'fa-file-pdf', 
    ppt: 'fa-file-powerpoint',
    pptx: 'fa-file-powerpoint'
  }
  return icons[type] || 'fa-file' 
}

// ============ 数据加载函数 ============

/**
 * 加载文件夹树
 */
const loadFolderTree = async () => {
  try {
    loading.value = true
    const response = await getCompanyFolderTree()
    // 将后端返回的数据转换为前端需要的格式
    categoryTree.value = transformFolderTree(response.data || [])
    if (categoryTree.value.length > 0) {
      expandedIds.value = [categoryTree.value[0].id]
    }
  } catch (error) {
    console.error('加载目录树失败:', error)
    $message.error('加载目录树失败：' + (error.message || '未知错误'))
  } finally {
    loading.value = false
  }
}

/**
 * 转换文件夹树数据格式
 */
const transformFolderTree = (folders) => {
  if (!folders || folders.length === 0) return []
  
  // 先转换为Map便于查找
  const folderMap = new Map()
  const result = []
  
  // 第一遍：创建所有节点
  folders.forEach(folder => {
    const node = {
      id: folder.Guid || folder.guid,
      name: folder.Title || folder.title,
      parentId: folder.ParentGuid || folder.parentGuid || null,
      docCount: folder.DocCount || folder.docCount || 0,
      children: []
    }
    folderMap.set(node.id, node)
  })
  
  // 第二遍：建立父子关系
  folderMap.forEach(node => {
    if (!node.parentId) {
      // 顶级节点
      result.push(node)
    } else {
      // 子节点
      const parent = folderMap.get(node.parentId)
      if (parent) {
        parent.children.push(node)
      } else {
        // 如果找不到父节点，也作为顶级节点
        result.push(node)
      }
    }
  })
  
  return result
}

/**
 * 加载指定目录下的文件
 */
const loadFiles = async (catalogGuid) => {
  try {
    loading.value = true
    const response = await getFilesList(catalogGuid, 'CompanyBox', {
      search: docSearch.value,
      fileType: docTypeFilter.value
    })
    
    // 将后端返回的数据转换为前端格式
    allDocs.value = (response.data || []).map(file => {
      const docId = file.Guid || file.guid
      // 清理文件类型，去除可能的点号前缀
      let docType = (file.ContentType || file.contentType || 'doc').toLowerCase()
      if (docType.startsWith('.')) {
        docType = docType.substring(1)
      }
      
      return {
        id: docId,
        name: file.Titles || file.titles || '未命名文档',
        code: file.Para2 || file.para2 || '',
        type: docType,
        version: file.Vesion || file.vesion || '1.0',
        status: file.Para3 || file.para3 || 'published',
        author: file.OwnerDspName || file.ownerDspName || file.OwnerAccount || file.ownerAccount || '未知',
        updateTime: formatDateTime(file.UpdateTime || file.updateTime || file.CreateTime || file.createTime),
        expireDate: file.Para4 ? formatDate(file.Para4) : (file.para4 ? formatDate(file.para4) : ''),
        size: formatFileSize(file.ContentLength || file.contentLength),
        categoryId: file.FolderGuid || file.folderGuid,
        savePath: file.SavePath || file.savePath || '',
        relatedFlow: file.Para5 || file.para5 || '',
        summary: file.Para6 || file.para6 || '',
        versions: []
      }
    })
    
    console.log('✅ 加载文件列表成功:', allDocs.value.length, '个文件')
  } catch (error) {
    console.error('❌ 加载文件列表失败:', error)
    $message.error('加载文件列表失败：' + (error.message || '未知错误'))
  } finally {
    loading.value = false
  }
}

/**
 * 格式化文件大小
 */
const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0 || isNaN(bytes)) return '-'
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
}

/**
 * 格式化日期时间
 */
const formatDateTime = (datetime) => {
  if (!datetime) return ''
  try {
    const d = new Date(datetime)
    if (isNaN(d.getTime())) return ''
    return d.toISOString().slice(0, 16).replace('T', ' ')
  } catch (e) {
    console.warn('日期格式化失败:', datetime, e)
    return ''
  }
}

/**
 * 格式化日期
 */
const formatDate = (date) => {
  if (!date) return ''
  try {
    const d = new Date(date)
    if (isNaN(d.getTime())) return ''
    return d.toISOString().slice(0, 10)
  } catch (e) {
    console.warn('日期格式化失败:', date, e)
    return ''
  }
}

// 组件挂载时加载数据
onMounted(() => {
  loadFolderTree()
})
</script>

<style scoped>
.view-container { padding: 16px; background: #f5f7fa; }
.main-layout { display: flex; gap: 16px; min-height: calc(94vh - 110px); max-height: calc(94vh - 110px); position: relative; }
.left-panel { flex: 0 0 24%; background: #fff; border-radius: 6px; border: 1px solid #e8ecf1; overflow: hidden; display: flex; flex-direction: column; }
.panel-header { padding: 10px 12px; border-bottom: 1px solid #e8ecf1; background: #fafbfc; display: flex; justify-content: space-between; align-items: center; }
.panel-header h4 { margin: 0; font-size: 13px; font-weight: 600; color: #374151; }
.panel-body { height: 100%; padding: 8px; overflow-y: auto; }
.right-panel { flex: 1; background: #fff; border-radius: 6px; border: 1px solid #e8ecf1; overflow: hidden; }

.tree-table { width: 100%; border-collapse: collapse; }
.tree-table thead th { padding: 8px 10px; font-size: 11px; font-weight: 600; color: #6b7280; background: #f8fafc; border-bottom: 1px solid #e8ecf1; text-align: left; }
.tree-table tbody td { padding: 7px 10px; border-bottom: 1px solid #f3f4f6; font-size: 12px; color: #374151; }
.tree-row { cursor: pointer; transition: background 0.1s; }
.tree-row:hover { background: #f9fafb; }
.tree-row.selected { background: #eff6ff; }
.tree-row.selected td { color: #2563eb; }
.tree-name-cell { display: flex; align-items: center; gap: 4px; }
.expand-icon { width: 14px; height: 14px; display: flex; align-items: center; justify-content: center; color: #9ca3af; font-size: 9px; cursor: pointer; }
.expand-icon:hover { color: #3b82f6; }
.expand-icon.expanded i { transform: rotate(90deg); }
.expand-placeholder { width: 14px; }
.tree-name-cell i { color: #3b82f6; font-size: 11px; }
.tree-name { margin-left: 2px; font-size: 12px; }
.count-cell { color: #9ca3af; font-size: 11px; text-align: center; }

.right-header { padding: 10px 12px; border-bottom: 1px solid #e8ecf1; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px; }
.header-left { display: flex; align-items: center; gap: 16px; }
.header-left h5 { margin: 0; font-size: 13px; font-weight: 600; color: #374151; white-space: nowrap; }
.header-search { display: flex; gap: 8px; }
.form-control-sm { padding: 5px 8px; border: 1px solid #e7eaec; border-radius: 4px; font-size: 12px; }

.doc-list { overflow-y: auto; }
.doc-item { display: flex; align-items: center; gap: 14px; padding: 12px 14px; border-bottom: 1px solid #f3f4f6; cursor: pointer; transition: background 0.15s; }
.doc-item:hover { background: #f9fafb; }
.doc-item.active { background: #eff6ff; }
.doc-icon { font-size: 24px; color: #3b82f6; width: 32px; text-align: center; }
.doc-info { flex: 1; min-width: 0; }
.doc-name { font-size: 13px; font-weight: 500; color: #374151; margin-bottom: 4px; }
.doc-meta { display: flex; gap: 12px; flex-wrap: wrap; }
.meta { font-size: 11px; color: #9ca3af; }
.doc-actions { display: flex; gap: 4px; }
.action-btn { width: 26px; height: 26px; border: 1px solid #e5e7eb; border-radius: 4px; background: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 11px; transition: all 0.15s; }
.action-btn.preview { color: #3b82f6; } .action-btn.preview:hover { background: #3b82f6; color: #fff; }
.action-btn.edit { color: #6366f1; } .action-btn.edit:hover { background: #6366f1; color: #fff; }
.action-btn.download { color: #10b981; } .action-btn.download:hover { background: #10b981; color: #fff; }
.action-btn.delete { color: #ef4444; } .action-btn.delete:hover { background: #ef4444; color: #fff; }

.empty-list { text-align: center; padding: 40px; color: #9ca3af; }
.empty-state { text-align: center; padding: 60px 20px; color: #9ca3af; }
.empty-state i { font-size: 48px; margin-bottom: 12px; color: #d1d5db; }
.empty-state p { font-size: 14px; margin-bottom: 4px; }
.empty-state .hint { font-size: 12px; color: #d1d5db; }

/* 右侧详情抽屉 */
.detail-drawer {
  position: absolute;
  top: 0;
  right: 0;
  width: 380px;
  height: 100%;
  background: #fff;
  border-left: 1px solid #e8ecf1;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  z-index: 100;
  display: flex;
  flex-direction: column;
}

.drawer-header {
  padding: 14px 16px;
  border-bottom: 1px solid #e8ecf1;
  background: #fafbfc;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.drawer-header h5 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 8px;
}

.drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;
}

.detail-item {
  margin-bottom: 8px;
}

.detail-item.full-width {
  grid-column: 1 / -1;
}

.detail-item label {
  font-size: 11px;
  color: #9ca3af;
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
}

.detail-item span {
  font-size: 13px;
  color: #374151;
  word-break: break-word;
}

.drawer-actions {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e8ecf1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.version-history {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #e8ecf1;
}

.version-history h6 {
  font-size: 12px;
  color: #374151;
  margin: 0 0 8px 0;
  font-weight: 600;
}

.version-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px solid #f3f4f6;
  font-size: 12px;
}

.version-tag {
  background: #ede9fe;
  color: #7c3aed;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 500;
  flex-shrink: 0;
}

.version-meta {
  margin-left: auto;
  color: #9ca3af;
  font-size: 11px;
  flex-shrink: 0;
}

/* 抽屉动画 */
.slide-left-enter-active,
.slide-left-leave-active {
  transition: transform 0.3s ease;
}

.slide-left-enter-from {
  transform: translateX(100%);
}

.slide-left-leave-to {
  transform: translateX(100%);
}

.close-btn {
  background: none;
  border: none;
  font-size: 18px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.close-btn:hover {
  color: #333;
}

.status-badge { display: inline-block; padding: 2px 8px; border-radius: 3px; font-size: 11px; font-weight: 500; }
.status-badge.published { background: #dcfce7; color: #16a34a; }
.status-badge.draft { background: #fef3c7; color: #d97706; }
.status-badge.archived { background: #e5e7eb; color: #6b7280; }
.status-badge.obsolete { background: #fee2e2; color: #dc2626; }

.upload-area { border: 2px dashed #d1d5db; border-radius: 8px; padding: 24px; text-align: center; cursor: pointer; color: #9ca3af; transition: all 0.2s; }
.upload-area:hover { border-color: #3b82f6; color: #3b82f6; }
.upload-area i { font-size: 32px; margin-bottom: 8px; }

.btn { padding: 6px 14px; border: 1px solid transparent; border-radius: 4px; font-size: 13px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; transition: all 0.2s; justify-content: center; }
.btn-primary { background: #3b82f6; color: #fff; border-color: #3b82f6; }
.btn-primary:hover { background: #2563eb; }
.btn-default { background: #fff; color: #374151; border-color: #d1d5db; }
.btn-default:hover { background: #f9fafb; }
.btn-xs { padding: 3px 8px; font-size: 11px; }
.btn-block { width: 100%; }
.modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal-dialog { background: #fff; border-radius: 8px; overflow-y: auto; max-height: 85vh; }
.modal-header { display: flex; justify-content: space-between; align-items: center; padding: 15px 20px; border-bottom: 1px solid #e7eaec; }
.modal-header h4 { margin: 0; font-size: 16px; }
.modal-close { background: none; border: none; font-size: 24px; cursor: pointer; color: #999; }
.modal-close:hover { color: #333; }
.modal-body { padding: 20px; }
.modal-footer { padding: 12px 20px; border-top: 1px solid #e7eaec; display: flex; justify-content: flex-end; gap: 10px; }
.form-row { display: flex; gap: 15px; margin-bottom: 15px; }
.form-group { flex: 1; }
.form-group label { display: block; font-size: 13px; color: #676a6c; margin-bottom: 5px; font-weight: 500; }
.form-group .form-control { width: 100%; padding: 8px 12px; border: 1px solid #e7eaec; border-radius: 4px; font-size: 13px; box-sizing: border-box; }
.form-group textarea.form-control { resize: vertical; }
</style>
